import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { RiWhatsappFill, RiPhoneFill } from 'react-icons/ri';
import '../sass/AdminPage.scss';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/admin`;

const STATUS_LABEL = {
  PENDING:  { label: 'Pendiente',  cls: 'badge-pending'  },
  ACTIVE:   { label: 'Activa',     cls: 'badge-active'   },
  REJECTED: { label: 'Rechazada',  cls: 'badge-rejected' },
  DELETED:  { label: 'Eliminada',  cls: 'badge-deleted'  },
};

function formatPrice(p) {
  if (!p) return '—';
  return '$' + Number(p).toLocaleString('es-CL');
}

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || { label: status, cls: 'badge-pending' };
  return <span className={`adm-badge ${s.cls}`}>{s.label}</span>;
}

// ── Modal de vista previa ─────────────────────────────────────────────────────
function ListingPreviewModal({ listing, onClose }) {
  const [imgIdx, setImgIdx] = useState(0);
  const photos = listing.imageUrls || [];
  const img = photos[imgIdx];

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const title = [listing.makerName, listing.modelName, listing.year].filter(Boolean).join(' ') || 'Sin título';

  const specs = [
    { icon: '📅', label: 'Año',         value: listing.year },
    { icon: '🔢', label: 'Kilómetros',  value: listing.mileage ? Number(listing.mileage).toLocaleString('es-CL') + ' km' : null },
    { icon: '⚙️', label: 'Transmisión', value: listing.transmission },
    { icon: '⛽', label: 'Combustible', value: listing.fuelType },
    { icon: '📍', label: 'Ciudad',      value: listing.city },
    { icon: '🗺️', label: 'Región',      value: listing.region },
    { icon: '🚗', label: 'Condición',   value: listing.condition === 'NEW' ? 'Nuevo' : listing.condition === 'USED' ? 'Usado' : null },
  ].filter(s => s.value);

  const cleanPhone = (p) => p ? p.replace(/\D/g, '') : null;
  const waUrl = () => {
    const clean = cleanPhone(listing.contactPhone);
    if (!clean) return null;
    const num = clean.startsWith('56') ? clean : `56${clean.startsWith('9') ? clean : '9' + clean}`;
    return `https://wa.me/${num}?text=${encodeURIComponent(`Hola, me interesa tu auto: ${title}`)}`;
  };

  return (
    <div className="preview-overlay" onClick={onClose}>
      <div className="preview-modal" onClick={e => e.stopPropagation()}>

        <div className="preview-modal-header">
          <div>
            <span className="preview-label">Vista previa</span>
            <StatusBadge status={listing.status} />
          </div>
          <button className="preview-close" onClick={onClose}>✕</button>
        </div>

        <div className="preview-body">
          {/* Fotos */}
          <div className="preview-photos">
            {photos.length > 0 ? (
              <>
                <div className="preview-main-img">
                  <img
                    src={img}
                    alt="foto"
                    onError={e => { e.target.src = 'https://placehold.co/800x450/1a1a1a/fff?text=Sin+foto'; }}
                  />
                  {photos.length > 1 && (
                    <>
                      <button className="prev-arrow left" onClick={() => setImgIdx(i => (i - 1 + photos.length) % photos.length)}>‹</button>
                      <button className="prev-arrow right" onClick={() => setImgIdx(i => (i + 1) % photos.length)}>›</button>
                      <span className="prev-counter">{imgIdx + 1} / {photos.length}</span>
                    </>
                  )}
                </div>
                {photos.length > 1 && (
                  <div className="preview-thumbs">
                    {photos.map((src, i) => (
                      <img
                        key={i} src={src} alt={`foto-${i}`}
                        className={i === imgIdx ? 'active' : ''}
                        onClick={() => setImgIdx(i)}
                        onError={e => { e.target.src = 'https://placehold.co/160x100/1a1a1a/fff?text=' + (i+1); }}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="preview-no-photo">📷 Sin fotos</div>
            )}
          </div>

          {/* Info */}
          <div className="preview-info">
            <h3 className="preview-title">{title}</h3>
            <div className="preview-price">{formatPrice(listing.price)}</div>

            <div className="preview-specs">
              {specs.map((s, i) => (
                <div key={i} className="spec-chip">
                  <span className="spec-icon">{s.icon}</span>
                  <span className="spec-label">{s.label}</span>
                  <span className="spec-value">{s.value}</span>
                </div>
              ))}
            </div>

            {listing.description && (
              <div className="preview-desc">
                <strong>Descripción</strong>
                <p>{listing.description}</p>
              </div>
            )}

            <div className="preview-contact">
              <strong>Contacto</strong>
              <div className="contact-name">{listing.contactName || '—'}</div>
              <div className="contact-phone">{listing.contactPhone || '—'}</div>
              <div className="contact-btns">
                {waUrl() && (
                  <a href={waUrl()} target="_blank" rel="noopener noreferrer" className="btn-wa">
                    <RiWhatsappFill /> WhatsApp
                  </a>
                )}
                {listing.contactPhone && (
                  <a href={`tel:${cleanPhone(listing.contactPhone)}`} className="btn-call">
                    <RiPhoneFill /> Llamar
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const ROLE_LABEL = { ADMIN: 'Admin', REVIEWER: 'Revisor', USER: 'Usuario' };
const ROLE_CLS   = { ADMIN: 'badge-active', REVIEWER: 'badge-pending', USER: 'badge-deleted' };

// ── Panel de usuarios ─────────────────────────────────────────────────────────
function UsersPanel({ uid }) {
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    fetch(`${API}/users?uid=${uid}`)
      .then(r => r.json()).then(setUsers).catch(() => {});
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  const changeRole = async (targetUid, role) => {
    setSaving(targetUid);
    try {
      await fetch(`${API}/roles?adminUid=${uid}&targetUid=${targetUid}&role=${role}`, { method: 'POST' });
      setMsg(`Rol actualizado.`);
      load();
      setTimeout(() => setMsg(''), 3000);
    } catch { setMsg('Error al guardar.'); }
    finally { setSaving(null); }
  };

  return (
    <div className="adm-section">
      <h3>Usuarios registrados</h3>
      {msg && <div className="adm-msg">{msg}</div>}

      <table className="adm-table">
        <thead>
          <tr><th>Usuario</th><th>Email</th><th>Último login</th><th>Rol</th></tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.uid}>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {u.photoUrl
                    ? <img src={u.photoUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} referrerPolicy="no-referrer" />
                    : <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#e63a3a', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>
                        {(u.displayName || u.email || '?')[0].toUpperCase()}
                      </span>
                  }
                  <span>{u.displayName || '—'}</span>
                </div>
              </td>
              <td style={{ fontSize: 13, color: '#555' }}>{u.email}</td>
              <td style={{ fontSize: 12, color: '#888' }}>
                {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('es-CL') : '—'}
              </td>
              <td>
                {u.role === 'ADMIN'
                  ? <span className="adm-badge badge-active">Admin</span>
                  : (
                    <select
                      className="select-status"
                      value={u.role}
                      disabled={saving === u.uid}
                      onChange={e => changeRole(u.uid, e.target.value)}
                    >
                      <option value="USER">Usuario</option>
                      <option value="REVIEWER">Revisor</option>
                    </select>
                  )
                }
              </td>
            </tr>
          ))}
          {users.length === 0 && (
            <tr><td colSpan={4} style={{ textAlign: 'center', color: '#aaa', padding: 30 }}>
              Aún no hay usuarios registrados.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const PAGE_SIZE = 20;

// ── Tabla de publicaciones ────────────────────────────────────────────────────
function ListingsPanel({ uid, role }) {
  const [listings, setListings]   = useState([]);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [brandFilter, setBrandFilter]   = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [page, setPage]                 = useState(1);
  const [loading, setLoading]           = useState(true);
  const [acting, setActing]             = useState(null);
  const [preview, setPreview]           = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/listings?uid=${uid}`);
      const data = await res.json();
      setListings(data);
    } catch { setListings([]); }
    finally { setLoading(false); }
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  // reset página cuando cambian filtros
  useEffect(() => { setPage(1); }, [statusFilter, brandFilter, dateFrom, dateTo]);

  const action = async (id, endpoint) => {
    setActing(id);
    try {
      await fetch(`${API}/listings/${id}/${endpoint}?uid=${uid}`, { method: 'POST' });
      load();
    } finally { setActing(null); }
  };

  const changeStatus = async (id, status) => {
    setActing(id);
    try {
      await fetch(`${API}/listings/${id}/status?uid=${uid}&status=${status}`, { method: 'PUT' });
      load();
    } finally { setActing(null); }
  };

  // ── Filtrado ──
  const filtered = listings.filter(l => {
    if (l.deletedAt) return false;
    if (statusFilter !== 'ALL' && l.status !== statusFilter) return false;
    if (brandFilter.trim()) {
      const b = brandFilter.trim().toLowerCase();
      const name = [l.makerName, l.modelName].filter(Boolean).join(' ').toLowerCase();
      if (!name.includes(b)) return false;
    }
    if (dateFrom && l.createdAt && new Date(l.createdAt) < new Date(dateFrom)) return false;
    if (dateTo   && l.createdAt && new Date(l.createdAt) > new Date(dateTo + 'T23:59:59')) return false;
    return true;
  });

  const pendingCount = listings.filter(l => l.status === 'PENDING').length;
  const totalPages   = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated    = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="adm-section">

      {/* Filtros de estado */}
      <div className="adm-filter-row">
        {['PENDING', 'ACTIVE', 'REJECTED', 'ALL'].map(f => (
          <button
            key={f}
            className={`btn-filter ${statusFilter === f ? 'active' : ''}`}
            onClick={() => setStatusFilter(f)}
          >
            {f === 'ALL' ? 'Todas' : STATUS_LABEL[f]?.label}
            {f === 'PENDING' && pendingCount > 0 && statusFilter !== 'PENDING' &&
              <span className="filter-count">{pendingCount}</span>
            }
          </button>
        ))}
      </div>

      {/* Filtros adicionales */}
      <div className="adm-extra-filters">
        <input
          type="text"
          placeholder="Buscar por marca o modelo..."
          value={brandFilter}
          onChange={e => setBrandFilter(e.target.value)}
        />
        <div className="adm-date-range">
          <label>Desde</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <label>Hasta</label>
          <input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)} />
          {(dateFrom || dateTo) &&
            <button className="btn-clear" onClick={() => { setDateFrom(''); setDateTo(''); }}>✕</button>
          }
        </div>
        <span className="adm-count">{filtered.length} resultado{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {preview && <ListingPreviewModal listing={preview} onClose={() => setPreview(null)} />}

      {loading ? (
        <div className="adm-spinner-wrap"><div className="adm-spinner" /></div>
      ) : paginated.length === 0 ? (
        <div className="adm-empty">No hay publicaciones con estos filtros.</div>
      ) : (
        <>
        <table className="adm-table">
          <thead>
            <tr>
              <th>ID</th><th>Vehículo</th><th>Precio</th><th>Contacto</th>
              <th>Estado</th><th>Fecha</th><th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map(l => (
              <tr key={l.id}>
                <td>#{l.id}</td>
                <td>
                  <strong>{[l.makerName, l.modelName, l.year].filter(Boolean).join(' ') || 'Sin título'}</strong>
                  {l.city && <div style={{ fontSize: 12, color: '#888' }}>{l.city}</div>}
                </td>
                <td>{formatPrice(l.price)}</td>
                <td>
                  <div style={{ fontSize: 13 }}>{l.contactName}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{l.contactPhone}</div>
                </td>
                <td><StatusBadge status={l.status} /></td>
                <td style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>
                  {l.createdAt ? new Date(l.createdAt).toLocaleDateString('es-CL') : '—'}
                </td>
                <td>
                  <div className="adm-actions">
                    <button className="btn-preview" onClick={() => setPreview(l)}>👁 Ver</button>
                    {l.status === 'PENDING' && (
                      <>
                        <button className="btn-approve" disabled={acting === l.id} onClick={() => action(l.id, 'approve')}>✓ Aprobar</button>
                        <button className="btn-reject"  disabled={acting === l.id} onClick={() => action(l.id, 'reject')}>✕ Rechazar</button>
                      </>
                    )}
                    {l.status !== 'PENDING' && role === 'ADMIN' && (
                      <select className="select-status" value={l.status} onChange={e => changeStatus(l.id, e.target.value)} disabled={acting === l.id}>
                        <option value="PENDING">Pendiente</option>
                        <option value="ACTIVE">Activa</option>
                        <option value="REJECTED">Rechazada</option>
                      </select>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="adm-pagination">
            <button className="btn-page" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Anterior</button>
            <span className="adm-page-info">Página {page} de {totalPages}</span>
            <button className="btn-page" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Siguiente ›</button>
          </div>
        )}
        </>
      )}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function AdminPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('listings');

  if (!user) {
    return (
      <div className="adm-page">
        <div className="adm-empty" style={{ padding: 60 }}>
          <h3>Debes iniciar sesión para acceder.</h3>
        </div>
      </div>
    );
  }

  if (role !== 'ADMIN' && role !== 'REVIEWER') {
    return (
      <div className="adm-page">
        <div className="adm-empty" style={{ padding: 60 }}>
          <h3>Sin permisos de acceso.</h3>
          <button className="btn-adm-primary" onClick={() => navigate('/')}>Volver al inicio</button>
        </div>
      </div>
    );
  }

  return (
    <div className="adm-page">
      <div className="adm-header">
        <div>
          <h2>Panel de administración</h2>
          <span className={`adm-badge ${role === 'ADMIN' ? 'badge-active' : 'badge-pending'}`}>
            {role === 'ADMIN' ? 'Administrador' : 'Revisor'}
          </span>
        </div>
      </div>

      <div className="adm-tabs">
        <button className={tab === 'listings' ? 'active' : ''} onClick={() => setTab('listings')}>
          Publicaciones
        </button>
        {role === 'ADMIN' && (
          <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}>
            Usuarios
          </button>
        )}
      </div>

      {tab === 'listings' && <ListingsPanel uid={user.uid} role={role} />}
      {tab === 'users'    && <UsersPanel uid={user.uid} />}
    </div>
  );
}
